import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Slide from '@material-ui/core/Slide';
import Toolbar from '@material-ui/core/Toolbar';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DoneIcon from '@material-ui/icons/Done';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Button, Tooltip } from '@totango-app-team/ui-shared-components';
import classnames from 'classnames';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';

import ConfirmationModal from 'Components/modals/confirmation-modal/confirmation-modal';
import useModalState from 'Hooks/useModalState';
import useSearchFocusShortcut from 'Hooks/useSearchFocusShortcut';

import './modal.less';

const DIVIDER_STYLE = { marginTop: 20 };
const FOOTER_DIVIDER_STYLE = { marginBottom: 20 };

function Modal(props) {
	const {
		isOpen,
		closeModal,
		onSubmit,
		onSubmitHover,
		headerIcon,
		title,
		message,
		cancelText,
		submitText,
		className,
		submitButtonClassName,
		submitButtonType,
		submitButtonDisabled,
		cancelButtonType,
		showCancel,
		hideFooter,
		hideHeader,
		showHeaderSubmitButton,
		showHeaderCloseButton,
		headerCloseButtonComponent,
		customHeader,
		cancelVariant,
		onCancel,
		customFooter,
		useBackButton,
		submitTooltip,
		isLoading,
		fullScreen,
		fullScreenOption,
		newTabLink,
		useTransition,
		modalEnteredEvent,
		unsavedChangesText,
		showUnsavedChanges,
		discardWarningMsg,
		onCancelButtonClick,
		backgroundGreyed,
		headerToolbar,
		dividers,
		savingStatusComponent,
		submitButtonVariant,
		maxWidth = 'lg',
		...rest
	} = props;
	const [isFullScreen, setIsFullScreen] = useState(fullScreen || false);
	const {
		isOpen: isConfirmationModalOpen,
		closeModal: closeConfirmationModal,
		openModal: openConfirmationModal,
	} = useModalState();
	const { addSearchFocusShortcut, removeSearchFocusShortcut } = useSearchFocusShortcut();
	const doCancelActions = useCallback(() => {
		if (showUnsavedChanges) {
			openConfirmationModal();
			return;
		}
		setIsFullScreen(fullScreen || false);
	}, [showUnsavedChanges, openConfirmationModal, fullScreen]);
	const handleCancelButtonClick = useCallback(() => {
		doCancelActions();
		if (onCancelButtonClick) {
			onCancelButtonClick();
		}
		closeModal();
	}, [doCancelActions, onCancelButtonClick, closeModal]);
	const onCloseModal = useCallback(() => {
		doCancelActions();
		if (onCancel) {
			onCancel();
			return;
		}
		closeModal();
	}, [doCancelActions, onCancel, closeModal]);

	const discardChanges = useCallback(() => {
		closeConfirmationModal();
		closeModal();
	}, [closeConfirmationModal, closeModal]);

	const toggleFullScreen = useCallback(() => setIsFullScreen(curr => !curr), []);

	const Transition = useMemo(
		() =>
			React.forwardRef((properties, ref) => (
				<Slide direction="down" ref={ref} {...properties} onEntered={modalEnteredEvent} />
			)),
		[modalEnteredEvent],
	);
	const BackdropStyle = useMemo(
		() => ({ classes: { root: `backdrop-root ${backgroundGreyed ? 'greyed' : ''}` } }),
		[backgroundGreyed],
	);

	useEffect(() => {
		if (isOpen) {
			removeSearchFocusShortcut();
		}
		return () => {
			addSearchFocusShortcut();
		};
	}, [addSearchFocusShortcut, isOpen, removeSearchFocusShortcut]);
	const classes = {
		root: classnames('confirmation-dialog', className, { 'full-screen': isFullScreen }),
		paper: classnames('confirmation-dialog-paper', { dividers }),
	};
	const closeButton = showHeaderCloseButton ? (
		<>
			{headerCloseButtonComponent || (
				<span className="material-icons close header-button" onClick={closeModal}>
					close
				</span>
			)}
		</>
	) : (
		''
	);
	const submitButton = (
		<Button
			onClick={onSubmit}
			onMouseEnter={onSubmitHover}
			className={`confirmation-dialog-button submit ${submitButtonClassName}`}
			isLoading={isLoading}
			variant={submitButtonVariant}
			color={submitButtonType}
			disabled={submitButtonDisabled}
		>
			{submitText}
		</Button>
	);
	return (
		<>
			<Dialog
				maxWidth={maxWidth ?? 'lg'}
				aria-labelledby="confirmation-dialog-title"
				open={isOpen}
				onClose={onCloseModal}
				classes={classes}
				fullScreen={isFullScreen}
				TransitionComponent={useTransition ? Transition : undefined}
				BackdropProps={BackdropStyle}
				{...rest}
			>
				{headerToolbar ? (
					<AppBar className="header-toolbar-container" component="div">
						<Toolbar className="header-toolbar">
							{headerToolbar}
							{submitButton}
							{closeButton}
						</Toolbar>
					</AppBar>
				) : (
					''
				)}
				{!hideHeader ? (
					<DialogTitle className={classnames('dialog-title', { 'header-Icon': headerIcon })}>
						{newTabLink && (
							<a
								className="new-tab-btn header-button"
								href={newTabLink}
								target="_blank"
								rel="noopener noreferrer"
							>
								<OpenInNewIcon className="material-icons" />
							</a>
						)}
						{fullScreenOption ? (
							<img
								className="full-screen-btn header-button"
								src={isFullScreen ? '/img/close-full-screen.svg' : '/img/open-in-full.svg'}
								alt="open in full"
								onClick={toggleFullScreen}
							/>
						) : (
							''
						)}
						{!useBackButton ? closeButton : ''}
						{showHeaderCloseButton && useBackButton && (
							<IconButton edge="start" color="inherit" aria-label="back-arrow" onClick={closeModal}>
								<ArrowBackIcon />
							</IconButton>
						)}
						{headerIcon}
						<div className="title">{title}</div>
						{showHeaderSubmitButton && (
							<IconButton
								className="header-submit-button"
								edge="start"
								color="inherit"
								onClick={onSubmit}
								disabled={submitButtonDisabled}
							>
								<DoneIcon />
							</IconButton>
						)}
					</DialogTitle>
				) : (
					customHeader
				)}
				{hideHeader && !headerToolbar && !customHeader ? closeButton : ''}
				{dividers ? <hr className="divider" style={DIVIDER_STYLE} /> : null}
				<DialogContent className="dialog-content">{message}</DialogContent>
				{dividers && (!hideFooter || customFooter) ? (
					<hr className="divider divider-bottom" style={FOOTER_DIVIDER_STYLE} />
				) : null}
				{!hideFooter && !customFooter ? (
					<DialogActions className="dialog-actions">
						{savingStatusComponent}
						{showUnsavedChanges && unsavedChangesText ? (
							<div className="unsaved-changes">{unsavedChangesText}</div>
						) : null}
						{showCancel ? (
							<Button
								className="confirmation-dialog-button cancel"
								color={cancelButtonType}
								variant={cancelVariant}
								onClick={onCancelButtonClick ? handleCancelButtonClick : onCloseModal}
							>
								{cancelText}
							</Button>
						) : (
							''
						)}
						<Tooltip title={submitTooltip || ''}>
							<div>{submitButton}</div>
						</Tooltip>
					</DialogActions>
				) : (
					customFooter
				)}
			</Dialog>

			<ConfirmationModal
				isOpen={isConfirmationModalOpen}
				closeModal={closeConfirmationModal}
				confirmationMessage={
					<>
						<strong>WARNING:</strong>
						{' You have unsaved changes'}
						<div className="sure-question">{discardWarningMsg}</div>
					</>
				}
				cancelText="Discard"
				onCancel={discardChanges}
				submitText="Go Back"
				onSubmit={closeConfirmationModal}
			/>
		</>
	);
}

Modal.defaultProps = {
	submitButtonType: 'primary',
	cancelButtonType: 'primary',
	cancelVariant: undefined,
	showCancel: true,
	hideFooter: false,
	hideHeader: false,
	showHeaderCloseButton: true,
	cancelText: 'Cancel',
	submitText: 'Okay',
	submitButtonDisabled: false,
	className: '',
	submitButtonClassName: '',
	title: '',
	onSubmit: noop,
	onCancel: null,
	customFooter: null,
	headerIcon: null,
	customHeader: null,
	submitTooltip: null,
	useBackButton: false,
	isLoading: false,
	useTransition: false,
	modalEnteredEvent: undefined,
	unsavedChangesText: undefined,
	disableBackdropClick: false,
	showUnsavedChanges: false,
	discardWarningMsg: 'Your changes will be lost if you leave.',
	fullScreenOption: false,
	newTabLink: null,
	onSubmitHover: noop,
	onCancelButtonClick: null,
	backgroundGreyed: true,
	dividers: false,
	closeModal: noop,
	savingStatusComponent: null,
	submitButtonVariant: 'contained',
	maxWidth: 'lg',
};

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	closeModal: PropTypes.func,
	onCancel: PropTypes.func,
	onSubmit: PropTypes.func,
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	className: PropTypes.string,
	submitButtonClassName: PropTypes.string,
	message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
	cancelText: PropTypes.string,
	submitText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	submitButtonType: PropTypes.oneOf(['default', 'primary', 'secondary']),
	cancelButtonType: PropTypes.oneOf(['default', 'primary', 'secondary']),
	cancelVariant: PropTypes.oneOf(['outlined', 'contained', 'text']),
	submitButtonVariant: PropTypes.oneOf(['outlined', 'contained', 'text']),
	disableBackdropClick: PropTypes.bool,
	backgroundGreyed: PropTypes.bool,
	showCancel: PropTypes.bool,
	hideFooter: PropTypes.bool,
	hideHeader: PropTypes.bool,
	showHeaderCloseButton: PropTypes.bool,
	headerIcon: PropTypes.element,
	customHeader: PropTypes.element,
	submitButtonDisabled: PropTypes.bool,
	customFooter: PropTypes.element,
	submitTooltip: PropTypes.string,
	useBackButton: PropTypes.bool,
	isLoading: PropTypes.bool,
	useTransition: PropTypes.bool,
	modalEnteredEvent: PropTypes.func,
	unsavedChangesText: PropTypes.string,
	showUnsavedChanges: PropTypes.bool,
	discardWarningMsg: PropTypes.string,
	fullScreenOption: PropTypes.bool,
	newTabLink: PropTypes.string,
	onSubmitHover: PropTypes.func,
	onCancelButtonClick: PropTypes.func,
	dividers: PropTypes.bool,
	savingStatusComponent: PropTypes.element,
	maxWidth: PropTypes.string,
};

export default Modal;
